import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { QuoteStatus } from "@assets/constants";
import { ArrayUtilities } from "@assets/utilities";
import type { RootState } from "@state/store";

export interface FilterQuoteState {
  search: string;
  status: QuoteStatus;
  shipmentTypes: string[];
  fromLocation: string;
  toLocation: string;
}

export const initialFilterQuotesState: FilterQuoteState = {
  search: "",
  status: QuoteStatus.All,
  shipmentTypes: [],
  fromLocation: "",
  toLocation: "",
};

export const filterQuoteSlice = createSlice({
  name: "filterQuote",
  initialState: initialFilterQuotesState,
  reducers: {
    filterQuoteSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    filterQuoteStatus: (state, action: PayloadAction<QuoteStatus>) => {
      state.status = action.payload;
    },
    filterQuoteShipmentType: (state, action: PayloadAction<string[]>) => {
      state.shipmentTypes = action.payload;
    },
    filterQuoteFromLocation: (state, action: PayloadAction<string | null>) => {
      state.fromLocation =
        action.payload || initialFilterQuotesState.fromLocation;
    },
    filterQuoteToLocation: (state, action: PayloadAction<string | null>) => {
      state.toLocation = action.payload || initialFilterQuotesState.toLocation;
    },
    filterQuoteReset: (state) => ({
      ...initialFilterQuotesState,
      search: state.search,
      status: state.status,
    }),
  },
});

export const {
  filterQuoteSearch,
  filterQuoteStatus,
  filterQuoteShipmentType,
  filterQuoteFromLocation,
  filterQuoteToLocation,
  filterQuoteReset,
} = filterQuoteSlice.actions;

export const filterQuoteState = (state: RootState) => state.filterQuoteSlice;

export const activeFilters = (state: RootState) =>
  Object.keys(state.filterQuoteSlice).filter((key) => {
    // Don't take into consideration search and status filters
    if (key === "search" || key === "status") {
      return false;
    }
    // If the value is an array, (ex "shipmentType") the helper is needed to check equality
    if (Array.isArray(state.filterQuoteSlice[key])) {
      return !ArrayUtilities.areEqual(
        state.filterQuoteSlice[key],
        initialFilterQuotesState[key]
      );
    } else {
      return state.filterQuoteSlice[key] !== initialFilterQuotesState[key];
    }
  });

export default filterQuoteSlice.reducer;
